import { Component, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { AlertController, ActionSheetController, ModalController, Platform, ViewWillEnter } from '@ionic/angular';
import { CalendarEvent, DayViewConfig, TimeSlotEvent, SMDragEndEvent } from './calendar/day-view-calendar/day-view-calendar.component';
import { CalendarService } from '../services/calendar.service';
import { UserService } from '../services/user.service';
import { Stylist, Service, Booking, ServiceDefinition } from '../models';
import { Utilities } from '../utilities/utilities';
import { List } from 'immutable';
import { SubscriptionLike } from 'rxjs';
import { Router } from '@angular/router';
import { NavigationStateService } from '../services/navigation-state.service';

export enum BookingFormActionType {
  AddFirstService = 0,
  AddAnotherService = 1,
  EditService = 2,
  AddWaitListBooking = 3,
  AddFirstServiceToWaitListBooking = 4
}

@Component({
  selector: 'smr-bookings',
  templateUrl: './bookings.page.html',
  styleUrls: ['./bookings.page.scss'],
})
export class BookingsPage implements OnInit, OnDestroy, ViewWillEnter {

  public calendar = {
    mode: 'day',
    currentDate: new Date()
  }

  public readOnlyStylistPicker: boolean;

  public stylist: Stylist;

  private firstLoad: boolean;

  public showFirstBookingPrompt: boolean;

  private onResumeSubscription: SubscriptionLike;

  private calendarRefreshIntervalRef;

  constructor(
    private actionSheetCtrl: ActionSheetController,
    private modalController: ModalController,
    private calendarService: CalendarService,
    private userService: UserService,
    private platform: Platform,
    private alertController: AlertController,
    private router: Router,
    private navigationStateService: NavigationStateService,
    private changeDetectorRef: ChangeDetectorRef) {
    this.firstLoad = true;
    //TODO
    // this.showFirstBookingPrompt = this.navParams.get('showFirstBookingPrompt');

    if (this.userService.isLoggedIn()) {
      const user: Stylist = this.userService.getUser();
      const calendarStylist: Stylist = this.calendarService.getBookingCalendarViewStylist();
      const calendarDate: Date = this.calendarService.getCalendarDate();
      this.onStylistSelect(calendarStylist ? calendarStylist : user);

      this.calendar.currentDate = calendarDate ? calendarDate : new Date();

      if (user.isAdministrator() || user.getViewAllCalendars() === 1) {
        this.readOnlyStylistPicker = false;
      } else {
        this.readOnlyStylistPicker = true;
      }
    }


  }

  public ionViewWillEnter() {
    this.calendarService.update();
    if (this.platform.is("capacitor")) {
      this.platform.ready().then(() => {
        this.onResumeSubscription = this.platform.resume.subscribe(() => {
          this.changeDetectorRef.markForCheck();
          // This prevents the app from accidentally showing the client picker
          // instead of the booking editor when the app is first opened on the phone
        });

      })
    }
    this.calendarService.update();
  }


  public ngOnInit() {
    // listen to the service worker promise in index.html to see if there has been a new update.
    // condition: the service-worker.js needs to have some kind of change - e.g. increment CACHE_VERSION.
    if (window['isUpdateAvailable']) {
      async isAvailable => {
        if (isAvailable) {


          const alert = await this.alertController.create({
            header: 'New Update available!',
            message: 'Please reload the app to continue.',
            buttons: [
              {
                text: 'Reload',
                handler: () => {
                  caches.delete("salonMonster-cache");
                  location.reload();
                }
              }]
          });
          alert.present();
        }
      };
    }

    // trigger calendar update every 5 minutes
    this.calendarRefreshIntervalRef = setInterval(() => {
      this.calendarService.update();
    }, 1000 * 60 * 5);

    // this.googleAnalytics.trackView('BookingCalendarPage')
    //   .then(() => {
    //   })
    //   .catch(() => {
    //   });
  }

  public ngOnDestroy() {
    if (this.onResumeSubscription) {
      this.onResumeSubscription.unsubscribe();
    }
    clearInterval(this.calendarRefreshIntervalRef);
  }


  public ionViewDidEnter() {

    if (window && window['Intercom']) {
      const hide = this.userService.getUser().getIsChatBubbleEnabled();
      window['Intercom']('update', { hide_default_launcher: !hide });
    }

    if (this.firstLoad === false) {
      if (this.calendarService.isOutdated()) {
      }
    } else {
      this.firstLoad = false;
    }

  }

  public ionViewDidLeave() {
  }

  public ngAfterViewInit() {
    if (this.showFirstBookingPrompt) {
      //TODO
      // this.modalController.create("FirstBookingPromptPage").present();
    }
  }

  public onEventsDidUpdate(success) {
    if (success) {
      this.calendarService.update();
    }
  }

  public onTimeSelected(selectedTimeSlot: TimeSlotEvent) {
    const salonID: number = this.userService.getSalon().getId();
    const stylist: Stylist = selectedTimeSlot.stylist;

    this.calendarService.setBookingCalendarViewStylist(stylist);

    const bookingOptions = {
      id: undefined,
      salonID: salonID,
      startDateTime: selectedTimeSlot.date,
      endDateTime: undefined,
      client: undefined,
      status: 3,
      services: List([]),
      recurring: undefined,
      reminderSent: undefined,
      creationDate: new Date(),
      sendEmail: 0,
      type: 'service',
      photos: List([]),
      beforePhotos: List([]),
      afterPhotos: List([])
    };

    let booking: Booking = new Booking(bookingOptions);

    this.navigationStateService.setState({
      booking: booking, actionType: BookingFormActionType.AddFirstService
    });

    this.router.navigate(['/bookings/booking-form-client-list'])
  }

  public onEventSelected($event: CalendarEvent, newDate?: Date) {
    const service: Service = $event.get('service');

    if (service.getType() === 'service') {

      this.calendarService.setBookingCalendarViewStylist(service.stylist);
      // this.navCtrl.push("BookingFormSummaryPage", {bookingID: $event.booking.getId(), newDate: newDate});
      this.navigationStateService.setState({
        bookingID: $event.booking.getId(),
        newDate: newDate
      });

      this.router.navigate(['/bookings/booking-form-summary/', $event.booking.getId()])
    } else if (service.getType() === 'timeoff') {
      this.navigationStateService.setState({
        booking: $event.booking.clone(),
        newDate: newDate
      });

      this.router.navigate(['/bookings/booking-form-time-off']);
    }
  }

  public async onEventDragEndHandler($event: SMDragEndEvent) {
    const actionSheet = await this.actionSheetCtrl.create({
      header: `Do you want to move this booking to ${Utilities.formatDate($event.newDate, 'dddd, MMMM D, Y @ h:mm a')}`,
      buttons: [
        {
          text: "Yes",
          handler: () => {

            actionSheet.dismiss().then(() => {
              this.onEventSelected($event.calendarEvent, $event.newDate);
            });

            return false;
          }
        },
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            actionSheet.dismiss().then(() => {
            });
            return false;
          }
        }
      ]
    });

    actionSheet.present();
  }

  public onStylistSelect(stylist: Stylist) {
    this.stylist = stylist;
    this.calendarService.setBookingCalendarViewStylist(stylist);
  }

  public onRangeChanged(views: List<DayViewConfig>) {
    if (views.count() > 0) {
      this.calendarService.setCalendarDate(views.first().date);
    }
  }

}
